import { cn } from '#client/lib/cn';

interface SpinnerProps {
  /** Always announced; only rendered visibly when `showLabel` is set. */
  label?: string | undefined;
  /**
   * Show the label as text beside the ring.
   *
   * Off by default: most spinners sit inside a button or a small cell where a
   * word of text would not fit, but the label must still exist for a screen
   * reader — an unlabelled `role="status"` announces nothing useful.
   */
  showLabel?: boolean | undefined;
  size?: 'sm' | 'md' | 'lg' | undefined;
  className?: string | undefined;
}

/**
 * Reserved for atomic blocking actions — a submitting button, a rendering PDF —
 * where there is no known layout to stand in for.
 *
 * Anything list- or form-shaped uses `Skeleton` instead, which mirrors the real
 * layout and reads as meaningfully faster.
 */
export function Spinner({
  label = 'Loading',
  showLabel = false,
  size = 'md',
  className,
}: SpinnerProps) {
  return (
    <span className={cn('c-spinner', `c-spinner--${size}`, className)} role="status">
      <span className="c-spinner__circle" aria-hidden="true" />
      <span className={showLabel ? 'c-spinner__label' : 'sr-only'}>{label}</span>
    </span>
  );
}
